import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';

const CARD =
    'bg-gray-900/80 border border-gray-800/90 rounded-2xl shadow-inner shadow-black/40';
const PANEL =
    'bg-gradient-to-b from-gray-950 via-gray-950/95 to-gray-900 border border-gray-800/90 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)]';

const TONE = {
    success: { wrap: 'bg-emerald-500/15 border border-emerald-400/40', text: 'text-emerald-300' },
    error: { wrap: 'bg-red-500/15 border border-red-400/40', text: 'text-red-300' },
    primary: { wrap: 'bg-cyan-500/15 border border-cyan-400/40', text: 'text-cyan-300' },
    info: { wrap: 'bg-blue-500/15 border border-blue-400/40', text: 'text-blue-300' },
    warning: { wrap: 'bg-amber-500/15 border border-amber-400/40', text: 'text-amber-300' }
};

const safeNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const formatMoney = (value, locale, currency) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 0 }).format(
        safeNumber(value)
    );

const formatDate = (dateString, locale) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(locale);
};

const calcSubtotal = (t) => {
    // ✅ Usa tus columnas reales (si algunas no vienen, safeNumber las pone en 0)
    const ingresos =
        safeNumber(t.tsv) +
        safeNumber(t.clientesConFactura) +
        safeNumber(t.clientesSinFactura) +
        safeNumber(t.isaacTSV) +
        safeNumber(t.costoRefaccionesCliente) +
        safeNumber(t.programasEliminaciones) +
        safeNumber(t.ventasVarios);

    const egresos = safeNumber(t.gastos) + safeNumber(t.costoRefaccionesProveedor);

    return ingresos - egresos;
};

const calcTaxes = ({ subtotal, includeIVA, includeISR, isIngreso, ivaRate = 0.16, isrRate = 0.0125 }) => {
    const iva = includeIVA ? subtotal * ivaRate : 0;
    // ISR en tu código original era solo para ingreso
    const isr = includeISR && isIngreso ? subtotal * isrRate : 0;
    const total = subtotal + iva - isr;
    return { iva, isr, total };
};

const EmptyState = ({ title = 'Sin datos', desc = 'Ajusta el rango o carga información.' }) => (
    <div className={CARD + ' p-6 flex items-center justify-center'}>
        <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-3">
                <Icon name="Database" size={22} className="text-cyan-300" />
            </div>
            <p className="text-white font-semibold">{title}</p>
            <p className="text-sm text-gray-400 mt-1">{desc}</p>
        </div>
    </div>
);

function TransactionModal({
    isOpen,
    onClose,
    onSubmit,
    initialValue,

    locale = 'es-MX',
    currency = 'MXN'
}) {
    const [form, setForm] = useState(() => ({
        type: 'ingreso',
        cliente: '',
        unidad: '',
        folio: '',
        fechaTrabajo: new Date().toISOString().split('T')[0],
        fechaFacturacion: '',
        fechaPago: '',
        tsv: 0,
        clientesConFactura: 0,
        clientesSinFactura: 0,
        isaacTSV: 0,
        gastos: 0,
        costoRefaccionesProveedor: 0,
        costoRefaccionesCliente: 0,
        programasEliminaciones: 0,
        ventasVarios: 0,
        status: 'pendiente',
        includeIVA: true,
        includeISR: true
    }));

    useEffect(() => {
        if (initialValue) {
            setForm((prev) => ({
                ...prev,
                ...initialValue,
                includeIVA: initialValue.includeIVA ?? true,
                includeISR: initialValue.includeISR ?? true
            }));
        } else {
            setForm((prev) => ({
                ...prev,
                type: 'ingreso',
                cliente: '',
                unidad: '',
                folio: '',
                fechaTrabajo: new Date().toISOString().split('T')[0],
                fechaFacturacion: '',
                fechaPago: '',
                tsv: 0,
                clientesConFactura: 0,
                clientesSinFactura: 0,
                isaacTSV: 0,
                gastos: 0,
                costoRefaccionesProveedor: 0,
                costoRefaccionesCliente: 0,
                programasEliminaciones: 0,
                ventasVarios: 0,
                status: 'pendiente',
                includeIVA: true,
                includeISR: true
            }));
        }
    }, [initialValue, isOpen]);

    const computed = useMemo(() => {
        const subtotal = calcSubtotal(form);
        const tax = calcTaxes({
            subtotal,
            includeIVA: !!form.includeIVA,
            includeISR: !!form.includeISR,
            isIngreso: form.type === 'ingreso'
        });
        return { subtotal, ...tax };
    }, [form]);

    if (!isOpen) return null;

    const inputClass =
        'w-full bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-sm text-gray-200 ' +
        'placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50';

    const labelClass = 'block text-xs font-semibold text-gray-300 mb-2';

    const setNum = (key, val) => setForm((p) => ({ ...p, [key]: safeNumber(val) }));

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit?.({
            ...form,
            subtotal: computed.subtotal,
            iva: computed.iva,
            isr: computed.isr,
            total: computed.total
        });
    };

    return (
        <div className="fixed inset-0 z-50 p-4 bg-black/80 backdrop-blur-xl flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 16, scale: 0.98 }}
                className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-800 bg-gray-950/95 shadow-[0_40px_120px_rgba(0,0,0,0.85)]"
            >
                <div className="p-4 sm:p-6 border-b border-gray-800 flex items-center justify-between">
                    <div>
                        <p className="text-white font-bold text-lg">
                            {initialValue ? 'Editar transacción' : 'Nueva transacción'}
                        </p>
                        <p className="text-xs text-gray-400">Los totales se recalculan automáticamente</p>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-900">
                        <Icon name="X" size={20} className="text-gray-400 hover:text-white" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
                    {/* Datos base */}
                    <div className={CARD + ' p-4 sm:p-5'}>
                        <div className="flex items-center gap-2 mb-4">
                            <div className={'w-9 h-9 rounded-xl flex items-center justify-center ' + TONE.primary.wrap}>
                                <Icon name="FileText" size={18} className={TONE.primary.text} />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-white">Datos</p>
                                <p className="text-[11px] text-gray-400">Cliente, folio, fechas y estatus</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <label className={labelClass}>Tipo</label>
                                <select
                                    value={form.type}
                                    onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                                    className={inputClass}
                                >
                                    <option value="ingreso">Ingreso</option>
                                    <option value="egreso">Egreso</option>
                                </select>
                            </div>

                            <div>
                                <label className={labelClass}>Cliente</label>
                                <input
                                    value={form.cliente}
                                    onChange={(e) => setForm((p) => ({ ...p, cliente: e.target.value }))}
                                    className={inputClass}
                                    required
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Unidad</label>
                                <input
                                    value={form.unidad}
                                    onChange={(e) => setForm((p) => ({ ...p, unidad: e.target.value }))}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Folio</label>
                                <input
                                    value={form.folio}
                                    onChange={(e) => setForm((p) => ({ ...p, folio: e.target.value }))}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Fecha trabajo</label>
                                <input
                                    type="date"
                                    value={form.fechaTrabajo}
                                    onChange={(e) => setForm((p) => ({ ...p, fechaTrabajo: e.target.value }))}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Estatus</label>
                                <select
                                    value={form.status}
                                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                                    className={inputClass}
                                >
                                    <option value="pendiente">Pendiente</option>
                                    <option value="pagado">Pagado</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Montos */}
                    <div className={CARD + ' p-4 sm:p-5'}>
                        <div className="flex items-center justify-between gap-3 mb-4">
                            <div className="flex items-center gap-2">
                                <div className={'w-9 h-9 rounded-xl flex items-center justify-center ' + TONE.info.wrap}>
                                    <Icon name="Calculator" size={18} className={TONE.info.text} />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-white">Montos</p>
                                    <p className="text-[11px] text-gray-400">Captura lo que venga del Excel/orden</p>
                                </div>
                            </div>

                            <div className="text-right">
                                <p className="text-[11px] text-gray-400">Total</p>
                                <p className="text-lg font-bold text-cyan-300 font-mono">
                                    {formatMoney(computed.total, locale, currency)}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            {[
                                ['tsv', 'TSV'],
                                ['clientesConFactura', 'Clientes con Factura'],
                                ['clientesSinFactura', 'Clientes sin Factura'],
                                ['isaacTSV', 'Isaac TSV'],
                                ['costoRefaccionesCliente', 'Refacciones (Cliente)'],
                                ['costoRefaccionesProveedor', 'Refacciones (Proveedor)'],
                                ['gastos', 'Gastos'],
                                ['programasEliminaciones', 'Programas/Eliminaciones'],
                                ['ventasVarios', 'Ventas varios']
                            ].map(([key, label]) => (
                                <div key={key}>
                                    <label className={labelClass}>{label}</label>
                                    <input
                                        type="number"
                                        value={safeNumber(form[key])}
                                        onChange={(e) => setNum(key, e.target.value)}
                                        className={inputClass}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setForm((p) => ({ ...p, includeIVA: !p.includeIVA }))}
                                className={[
                                    'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-all',
                                    form.includeIVA
                                        ? 'bg-emerald-500/15 border-emerald-400/60 text-emerald-200'
                                        : 'bg-gray-950/40 border-gray-700 text-gray-300 hover:border-emerald-400/50'
                                ].join(' ')}
                            >
                                <span className="inline-flex h-4 w-7 items-center rounded-full bg-black/60 border border-gray-600 p-0.5">
                                    <span
                                        className={[
                                            'h-3 w-3 rounded-full transition-transform',
                                            form.includeIVA ? 'translate-x-3 bg-emerald-400' : 'bg-gray-400'
                                        ].join(' ')}
                                    />
                                </span>
                                IVA 16%
                            </button>

                            <button
                                type="button"
                                onClick={() => setForm((p) => ({ ...p, includeISR: !p.includeISR }))}
                                className={[
                                    'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-all',
                                    form.includeISR
                                        ? 'bg-sky-500/15 border-sky-400/60 text-sky-200'
                                        : 'bg-gray-950/40 border-gray-700 text-gray-300 hover:border-sky-400/50'
                                ].join(' ')}
                            >
                                <span className="inline-flex h-4 w-7 items-center rounded-full bg-black/60 border border-gray-600 p-0.5">
                                    <span
                                        className={[
                                            'h-3 w-3 rounded-full transition-transform',
                                            form.includeISR ? 'translate-x-3 bg-sky-400' : 'bg-gray-400'
                                        ].join(' ')}
                                    />
                                </span>
                                ISR (solo ingreso)
                            </button>
                        </div>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="p-3 rounded-2xl bg-gray-950/60 border border-gray-800">
                                <p className="text-[11px] text-gray-400">Subtotal</p>
                                <p className="text-gray-100 font-mono font-bold">{formatMoney(computed.subtotal, locale, currency)}</p>
                            </div>
                            <div className="p-3 rounded-2xl bg-gray-950/60 border border-gray-800">
                                <p className="text-[11px] text-gray-400">IVA</p>
                                <p className="text-cyan-200 font-mono font-bold">{formatMoney(computed.iva, locale, currency)}</p>
                            </div>
                            <div className="p-3 rounded-2xl bg-gray-950/60 border border-gray-800">
                                <p className="text-[11px] text-gray-400">ISR</p>
                                <p className="text-sky-200 font-mono font-bold">{formatMoney(computed.isr, locale, currency)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-gray-700 text-gray-300 hover:bg-gray-900"
                        >
                            Cancelar
                        </Button>
                        <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white">
                            <Icon name="Save" className="w-4 h-4 mr-2" />
                            {initialValue ? 'Actualizar' : 'Guardar'}
                        </Button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}

const IncomeStatement = ({
    dateRange,
    onDateRangeChange,

    transactions = [], // ✅ data real
    loading = false,
    error = null,

    onCreate, // async (payload) => void
    onUpdate, // async (id, payload) => void
    onDelete, // async (id) => void
    onExport, // (payload) => void

    locale = 'es-MX',
    defaultCurrency = 'MXN'
}) => {
    const [currency, setCurrency] = useState(defaultCurrency);
    const [activeTab, setActiveTab] = useState('overview');

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const metrics = useMemo(() => {
        const totalIngresos = transactions.reduce((sum, t) => sum + (t.type === 'ingreso' ? safeNumber(t.total) : 0), 0);
        const totalEgresos = transactions.reduce((sum, t) => sum + (t.type === 'egreso' ? safeNumber(t.total) : 0), 0);
        const totalTSV = transactions.reduce((sum, t) => sum + safeNumber(t.tsv), 0);
        const totalClientesConFactura = transactions.reduce((sum, t) => sum + safeNumber(t.clientesConFactura), 0);
        const totalRefacciones = transactions.reduce((sum, t) => sum + safeNumber(t.costoRefaccionesCliente), 0);
        const neto = totalIngresos - totalEgresos;

        return { totalIngresos, totalEgresos, totalTSV, totalClientesConFactura, totalRefacciones, neto };
    }, [transactions]);

    const pct = (value, base = metrics.totalIngresos) => (base > 0 ? ((value / base) * 100).toFixed(1) : '0.0');

    const openNew = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (t) => {
        setEditing(t);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditing(null);
    };

    const submitModal = async (payload) => {
        if (editing?.id != null) {
            await onUpdate?.(editing.id, payload);
        } else {
            await onCreate?.(payload);
        }
        closeModal();
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <div className={PANEL + ' p-5'}>
                    <div className="h-6 w-64 bg-gray-800/70 rounded-lg" />
                    <div className="h-4 w-80 bg-gray-900/70 rounded-lg mt-2" />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={CARD + ' p-5'}>
                            <div className="h-11 w-11 bg-gray-800/60 rounded-xl mb-3" />
                            <div className="h-7 w-40 bg-gray-800/60 rounded-lg" />
                            <div className="h-4 w-32 bg-gray-900/60 rounded-lg mt-2" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) return <EmptyState title="Error al cargar" desc={String(error)} />;

    if (!transactions || transactions.length === 0) {
        return <EmptyState title="Sin transacciones" desc="No hay movimientos en este período." />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={PANEL + ' p-4 sm:p-5'}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg sm:text-xl font-bold text-white truncate">Estado de Resultados</h3>
                            <span className="inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/40">
                                {dateRange?.start || '—'} → {dateRange?.end || '—'}
                            </span>
                        </div>
                        <p className="text-cyan-200/80 text-xs sm:text-sm truncate">
                            Resumen, transacciones y análisis basado en datos reales
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                        <div className="inline-flex items-center gap-1 bg-gray-950/50 border border-gray-800 rounded-xl p-1">
                            {[
                                { id: 'overview', label: 'Resumen', icon: 'LayoutDashboard' },
                                { id: 'transactions', label: 'Transacciones', icon: 'List' },
                                { id: 'analysis', label: 'Análisis', icon: 'PieChart' }
                            ].map((t) => {
                                const active = activeTab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setActiveTab(t.id)}
                                        className={[
                                            'px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-2',
                                            active
                                                ? 'bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.25)]'
                                                : 'text-gray-300 hover:text-white hover:bg-gray-900/70'
                                        ].join(' ')}
                                    >
                                        <Icon name={t.icon} className="w-4 h-4" />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>

                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-xs sm:text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        >
                            <option value="MXN">MXN $</option>
                            <option value="USD">USD $</option>
                            <option value="ARS">ARS $</option>
                        </select>

                        <Button
                            variant="outline"
                            onClick={() =>
                                onExport?.({
                                    reportType: 'income-statement',
                                    range: dateRange,
                                    currency
                                })
                            }
                            className="border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white"
                        >
                            <Icon name="Download" className="w-4 h-4 mr-2" />
                            Exportar
                        </Button>

                        <Button
                            onClick={openNew}
                            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/30"
                        >
                            <Icon name="Plus" className="w-4 h-4 mr-2" />
                            Nueva
                        </Button>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: 'Ingresos Totales', value: metrics.totalIngresos, pct: '100%', icon: 'DollarSign', tone: 'success' },
                    { label: 'Egresos Totales', value: metrics.totalEgresos, pct: pct(metrics.totalEgresos) + '%', icon: 'TrendingDown', tone: 'error' },
                    { label: 'Utilidad Neta', value: metrics.neto, pct: pct(metrics.neto) + '%', icon: 'Target', tone: 'primary' },
                    { label: 'Total TSV', value: metrics.totalTSV, pct: pct(metrics.totalTSV) + '%', icon: 'BarChart3', tone: 'info' }
                ].map((m, i) => {
                    const st = TONE[m.tone];
                    return (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className={CARD + ' p-4 sm:p-5'}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className={'w-11 h-11 rounded-xl flex items-center justify-center ' + st.wrap}>
                                    <Icon name={m.icon} size={20} className={st.text} />
                                </div>
                                <span className="text-xs text-gray-400 font-mono">{m.pct}</span>
                            </div>
                            <p className="text-lg sm:text-2xl font-bold text-white mb-1 font-mono">
                                {formatMoney(m.value, locale, currency)}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400">{m.label}</p>
                        </motion.div>
                    );
                })}
            </div>

            <AnimatePresence mode="wait">
                {activeTab === 'overview' && (
                    <motion.div
                        key="overview"
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 12 }}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                    >
                        <div className={CARD + ' p-4 sm:p-6'}>
                            <div className="flex items-center gap-2 mb-4">
                                <div className={'w-9 h-9 rounded-xl flex items-center justify-center ' + TONE.success.wrap}>
                                    <Icon name="TrendingUp" size={18} className={TONE.success.text} />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-white">Flujo de ingresos</p>
                                    <p className="text-[11px] text-gray-400">Desglose real basado en columnas</p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                {[
                                    { label: 'Servicios TSV', value: metrics.totalTSV },
                                    { label: 'Clientes con Factura', value: metrics.totalClientesConFactura },
                                    { label: 'Venta de Refacciones', value: metrics.totalRefacciones }
                                ].map((it) => (
                                    <div key={it.label} className="flex items-center justify-between">
                                        <span className="text-xs sm:text-sm text-gray-400">{it.label}</span>
                                        <span className="text-xs sm:text-sm text-gray-100 font-mono font-semibold">
                                            {formatMoney(it.value, locale, currency)}
                                        </span>
                                    </div>
                                ))}
                                <div className="pt-3 mt-2 border-t border-gray-800 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-100">Total ingresos</span>
                                    <span className="text-emerald-300 font-mono font-bold">
                                        {formatMoney(metrics.totalIngresos, locale, currency)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={CARD + ' p-4 sm:p-6'}>
                            <div className="flex items-center gap-2 mb-4">
                                <div className={'w-9 h-9 rounded-xl flex items-center justify-center ' + TONE.primary.wrap}>
                                    <Icon name="PieChart" size={18} className={TONE.primary.text} />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-white">Rentabilidad</p>
                                    <p className="text-[11px] text-gray-400">Métricas calculadas con tus datos</p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs sm:text-sm text-gray-400">Margen neto</span>
                                    <span className="text-xs sm:text-sm text-cyan-200 font-mono font-semibold">
                                        {pct(metrics.neto)}%
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs sm:text-sm text-gray-400">Egresos / Ingresos</span>
                                    <span className="text-xs sm:text-sm text-gray-100 font-mono font-semibold">
                                        {pct(metrics.totalEgresos)}%
                                    </span>
                                </div>
                                <div className="pt-3 mt-2 border-t border-gray-800 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-100">Utilidad neta</span>
                                    <span className="text-cyan-300 font-mono font-bold">
                                        {formatMoney(metrics.neto, locale, currency)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'transactions' && (
                    <motion.div
                        key="transactions"
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 12 }}
                        className={CARD + ' overflow-hidden'}
                    >
                        <div className="p-4 sm:p-6 border-b border-gray-800 flex items-center justify-between">
                            <div>
                                <p className="text-white font-semibold">Registro de transacciones</p>
                                <p className="text-xs text-gray-400">{transactions.length} transacción(es)</p>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[980px]">
                                <thead>
                                    <tr className="border-b border-gray-800">
                                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Cliente</th>
                                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Folio</th>
                                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Fecha</th>
                                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">TSV</th>
                                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Total</th>
                                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Estado</th>
                                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">Acciones</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {transactions.map((t) => {
                                        const paid = t.status === 'pagado';
                                        return (
                                            <tr key={t.id} className="border-b border-gray-800/50 last:border-0 hover:bg-gray-950/40">
                                                <td className="py-3 px-4">
                                                    <p className="text-sm font-semibold text-gray-100">{t.cliente || '—'}</p>
                                                    <p className="text-xs text-gray-400">{t.unidad || '—'}</p>
                                                </td>
                                                <td className="py-3 px-4 text-sm text-gray-300">{t.folio || '—'}</td>
                                                <td className="py-3 px-4 text-sm text-gray-300">{formatDate(t.fechaTrabajo, locale)}</td>
                                                <td className="py-3 px-4 text-sm text-right text-blue-300 font-mono font-semibold">
                                                    {formatMoney(t.tsv, locale, currency)}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-right font-mono font-bold">
                                                    <span className={t.type === 'ingreso' ? 'text-emerald-300' : 'text-red-300'}>
                                                        {formatMoney(t.total, locale, currency)}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    <span
                                                        className={[
                                                            'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border',
                                                            paid
                                                                ? 'bg-emerald-500/10 text-emerald-200 border-emerald-500/30'
                                                                : 'bg-amber-500/10 text-amber-200 border-amber-500/30'
                                                        ].join(' ')}
                                                    >
                                                        {paid ? 'Pagado' : 'Pendiente'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => openEdit(t)}
                                                            className="p-2 rounded-xl hover:bg-gray-900 text-cyan-300"
                                                            title="Editar"
                                                        >
                                                            <Icon name="Edit" size={16} />
                                                        </button>
                                                        <button
                                                            onClick={() => onDelete?.(t.id)}
                                                            className="p-2 rounded-xl hover:bg-red-500/10 text-red-300"
                                                            title="Eliminar"
                                                        >
                                                            <Icon name="Trash2" size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'analysis' && (
                    <motion.div
                        key="analysis"
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 12 }}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                    >
                        <div className={CARD + ' p-4 sm:p-6'}>
                            <div className="flex items-center gap-2 mb-4">
                                <div className={'w-9 h-9 rounded-xl flex items-center justify-center ' + TONE.primary.wrap}>
                                    <Icon name="PieChart" size={18} className={TONE.primary.text} />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-white">Distribución de ingresos</p>
                                    <p className="text-[11px] text-gray-400">Porcentaje sobre ingresos totales</p>
                                </div>
                            </div>

                            {[
                                { category: 'Servicios TSV', amount: metrics.totalTSV, tone: 'info' },
                                { category: 'Clientes con Factura', amount: metrics.totalClientesConFactura, tone: 'success' },
                                { category: 'Refacciones', amount: metrics.totalRefacciones, tone: 'primary' }
                            ].map((it) => {
                                const width = metrics.totalIngresos > 0 ? (it.amount / metrics.totalIngresos) * 100 : 0;
                                const st = TONE[it.tone];
                                return (
                                    <div key={it.category} className="space-y-2 mb-3">
                                        <div className="flex justify-between text-xs sm:text-sm">
                                            <span className="text-gray-400">{it.category}</span>
                                            <span className="text-gray-100 font-mono">
                                                {formatMoney(it.amount, locale, currency)} • {pct(it.amount)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-950/60 border border-gray-800 rounded-full h-2 overflow-hidden">
                                            <div className="h-2 rounded-full" style={{ width: `${width}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className={CARD + ' p-4 sm:p-6'}>
                            <div className="flex items-center gap-2 mb-4">
                                <div className={'w-9 h-9 rounded-xl flex items-center justify-center ' + TONE.warning.wrap}>
                                    <Icon name="Gauge" size={18} className={TONE.warning.text} />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-white">Indicadores clave</p>
                                    <p className="text-[11px] text-gray-400">Sin números inventados</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    {
                                        label: 'Ticket promedio',
                                        value: formatMoney(metrics.totalIngresos / Math.max(transactions.filter(t => t.type === 'ingreso').length, 1), locale, currency)
                                    },
                                    {
                                        label: 'Ingresos / transacción',
                                        value: formatMoney(metrics.totalIngresos / Math.max(transactions.length, 1), locale, currency)
                                    },
                                    { label: 'Margen neto', value: pct(metrics.neto) + '%' },
                                    { label: 'Transacciones', value: String(transactions.length) }
                                ].map((m) => (
                                    <div key={m.label} className="p-3 rounded-2xl bg-gray-950/60 border border-gray-800 text-center">
                                        <p className="text-lg font-bold text-gray-100 font-mono">{m.value}</p>
                                        <p className="text-[11px] text-gray-400 mt-1">{m.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <TransactionModal
                isOpen={modalOpen}
                onClose={closeModal}
                onSubmit={submitModal}
                initialValue={editing}
                locale={locale}
                currency={currency}
            />
        </div>
    );
};

export default IncomeStatement;
